import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { api } from '../utils/api';
import { Package, TrendingUp, TrendingDown, AlertTriangle, DollarSign, Wifi, WifiOff } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import { useWebSocketContext } from '../context/WebSocketContext';

export const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isConnected, subscribe } = useWebSocketContext();

  const fetchData = React.useCallback(async () => {
    console.log('🔄 [Dashboard] fetchData() INICIADO');
    try {
      // NO usar setLoading en refresh automático para evitar parpadeos
      console.log('📡 [Dashboard] Haciendo peticiones API...');
      
      const [statsRes, productsRes, movementsRes] = await Promise.all([
        api.getDashboardStats(),
        api.getProducts(),
        api.getMovements()
      ]);
      
      console.log('✅ [Dashboard] Respuestas API recibidas');
      console.log('   - Stats:', statsRes.data);
      console.log('   - Products count:', productsRes.data.length);
      console.log('   - Movements count:', movementsRes.data.length);
      
      setStats(statsRes.data);
      setProducts(productsRes.data);
      setMovements(movementsRes.data);
      setLoading(false);
      
      console.log('✅ [Dashboard] Estado React ACTUALIZADO');
    } catch (error) {
      console.error('❌ [Dashboard] Error en fetchData:', error);
      // No mostrar toast en actualizaciones automáticas
    } finally {
      console.log('🏁 [Dashboard] fetchData() COMPLETADO');
    }
  }, []);

  // Carga inicial
  useEffect(() => {
    setLoading(true);
    fetchData();
  }, [fetchData]);

  // POLLING DESACTIVADO - causaba recargas visuales y pérdida de foco
  // useEffect(() => {
  //   const pollingInterval = setInterval(() => {
  //     fetchData();
  //   }, 5000);
  //   return () => clearInterval(pollingInterval);
  // }, [fetchData]);

  // Subscribe to WebSocket updates (instantáneo)
  useEffect(() => {
    console.log('🔌 [Dashboard] Suscribiéndose a WebSocket...');
    
    const unsubscribe = subscribe((message) => {
      console.log('========================================');
      console.log('📊 [Dashboard] HANDLER WebSocket ejecutado');
      console.log('📨 Mensaje recibido:', message);
      console.log('🔍 Tipo de mensaje:', message.type);
      
      if (message.type === 'stock_update') {
        console.log('✅ [Dashboard] Tipo correcto: stock_update');
        console.log('🔄 [Dashboard] Llamando a fetchData()...');
        
        fetchData(); // Ahora esto funcionará correctamente
        
        const productName = message.product?.name || 'Producto';
        const movementType = message.movement?.type === 'entry' ? 'Entrada' : 'Salida';
        
        console.log(`📢 [Dashboard] Mostrando toast: ${movementType}: ${productName}`);
        
        toast.info(`${movementType}: ${productName}`, {
          description: 'Inventario actualizado',
          duration: 2000
        });
      } else {
        console.log(`⚠️ [Dashboard] Tipo de mensaje NO coincide: esperaba 'stock_update', recibió '${message.type}'`);
      }
      console.log('========================================');
    });

    console.log('✅ [Dashboard] Suscripción WebSocket completada');
    
    return () => {
      console.log('🔌 [Dashboard] Desuscribiéndose de WebSocket...');
      unsubscribe();
    };
  }, [subscribe, fetchData]);

  const lowStockProducts = products.filter(p => p.stock <= p.min_stock);

  const chartData = [
    { name: 'Entradas', cantidad: stats?.recent_entries || 0 },
    { name: 'Salidas', cantidad: stats?.recent_exits || 0 },
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="h-10 w-48 bg-slate-200 dark:bg-slate-700 rounded-lg animate-pulse"></div>
            <div className="h-4 w-64 bg-slate-200 dark:bg-slate-700 rounded mt-3 animate-pulse"></div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => (
            <div key={i} className="h-28 bg-slate-200 dark:bg-slate-700 rounded-xl animate-pulse"></div>
          ))}
        </div>
        <div className="h-64 bg-slate-200 dark:bg-slate-700 rounded-xl animate-pulse"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 dark:text-white">Dashboard</h1>
          <p className="text-base text-slate-600 dark:text-slate-300 mt-2">Resumen general del inventario</p>
        </div>
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${
          isConnected 
            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' 
            : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
        }`}>
          {isConnected ? (
            <>
              <Wifi className="h-4 w-4" />
              <span className="font-medium">Tiempo Real</span>
            </>
          ) : (
            <>
              <WifiOff className="h-4 w-4" />
              <span className="font-medium">Desconectado</span>
            </>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-2 border-blue-300 dark:border-blue-700 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900" data-testid="stat-products">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-bold text-blue-900 dark:text-blue-50">Total Productos</CardTitle>
            <Package className="h-5 w-5 text-blue-700 dark:text-blue-300" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-mono font-bold text-blue-900 dark:text-white">{stats?.total_products || 0}</div>
          </CardContent>
        </Card>

        <Card className="border-2 border-green-300 dark:border-green-700 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950 dark:to-green-900" data-testid="stat-value">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-bold text-green-900 dark:text-green-50">Valor Total</CardTitle>
            <DollarSign className="h-5 w-5 text-green-700 dark:text-green-300" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-mono font-bold text-green-700 dark:text-green-300">€{stats?.total_value || 0}</div>
          </CardContent>
        </Card>

        <Card className="border-2 border-red-300 dark:border-red-700 bg-gradient-to-br from-red-50 to-red-100 dark:from-red-950 dark:to-red-900" data-testid="stat-alerts">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-bold text-red-900 dark:text-red-50">Alertas Stock</CardTitle>
            <AlertTriangle className="h-5 w-5 text-red-700 dark:text-red-300" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-mono font-bold text-red-700 dark:text-red-300">
              {stats?.low_stock_alerts || 0}
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-purple-300 dark:border-purple-700 bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-950 dark:to-purple-900" data-testid="stat-movements">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-bold text-purple-900 dark:text-purple-50">Movimientos</CardTitle>
            <TrendingUp className="h-5 w-5 text-purple-700 dark:text-purple-300" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-mono font-bold text-purple-900 dark:text-white">{movements.length}</div>
          </CardContent>
        </Card>
      </div>

      {/* Chart */}
      <Card className="border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
        <CardHeader>
          <CardTitle>Movimientos de Stock</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="cantidad" fill="#002FA7" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Low Stock Alerts */}
      {lowStockProducts.length > 0 && (
        <Card className="border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-900 dark:text-red-100">
              <AlertTriangle className="text-red-600 dark:text-red-400" />
              Productos con Stock Bajo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {lowStockProducts.slice(0, 5).map(product => (
                <div key={product.id} className="flex justify-between items-center p-3 bg-white dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-sm">
                  <div>
                    <p className="font-medium text-slate-900 dark:text-slate-100">{product.name}</p>
                    <p className="text-sm text-slate-600 dark:text-slate-400">Código: {product.barcode}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono font-bold text-red-600 dark:text-red-400 text-xl">{product.stock}</p>
                    <p className="text-xs text-slate-600 dark:text-slate-400">Mín: {product.min_stock}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};